/**
 * The words board E draws for an exercise outside the catalogue: the proposal's four facts (the
 * open row's strip, and one line each in „Dodaj wszystkie szybko"), and what a resolved row says
 * happened. The proposal arrives in the wire's spelling, so the taxonomy's own readers name each
 * value; the equipment's code is the mobile catalogue's key. `t` arrives as a parameter.
 */

import type { TFunction } from 'i18next';

import type { AiProposedExercise, AiUnresolvedExercise } from '~/entities/ai-conversation';
import { exerciseTypeFromApi, muscleGroupFromApi, muscleGroupLabelKey } from '~/entities/exercise-library';
import { sortMeasurementFields } from '~/shared/measurement';

import type { AddFailure } from '../model/exerciseDecisions';
import { type RowOutcome } from '../model/outsideCatalogue';

import { FACT_SEPARATOR } from './planWording';

/** The strip's four values: Typ, Partia główna, Sprzęt, Metryki serii. */
export interface ProposalFacts {
  readonly type: string;
  readonly muscle: string;
  readonly equipment: string;
  readonly metrics: string;
}

const LIST_SEPARATOR = ', ';

export function proposalFacts(t: TFunction, proposal: AiProposedExercise): ProposalFacts {
  const type = exerciseTypeFromApi(proposal.exerciseType);
  const muscle = muscleGroupFromApi(proposal.primaryMuscle);
  return {
    type: t(`web:exerciseLibrary.type.${type ?? 'unknown'}`),
    muscle: muscle === null ? t('web:aiPlan.unresolved.noMuscle') : t(muscleGroupLabelKey(muscle)),
    equipment:
      proposal.equipment.length === 0
        ? t('web:aiPlan.unresolved.noEquipment')
        : proposal.equipment
            .map((piece) => t(`exerciseLibrary.equipment.${piece.code}`, { defaultValue: t('exerciseLibrary.equipment.other') }))
            .join(LIST_SEPARATOR),
    metrics:
      proposal.measurementFields.length === 0
        ? t('web:aiPlan.unresolved.noMetrics')
        : sortMeasurementFields(proposal.measurementFields)
            .map((field) => t(`exerciseLibrary.createWizard.category.measurement.${field}.title`))
            .join(LIST_SEPARATOR),
  };
}

/** „Siłowe · Barki · Hantle · Ciężar, Powtórzenia": one name's line in the batch dialog. */
export function proposalLine(t: TFunction, proposal: AiProposedExercise): string {
  const facts = proposalFacts(t, proposal);
  return [facts.type, facts.muscle, facts.equipment, facts.metrics].join(FACT_SEPARATOR);
}

/** The resolved row's title and the line under it. */
export function outcomeWords(t: TFunction, exercise: AiUnresolvedExercise, outcome: RowOutcome): { readonly title: string; readonly sub: string } {
  const name = exercise.name;
  switch (outcome.kind) {
    case 'skipped':
      return { title: t('web:aiPlan.unresolved.done.skipped', { name }), sub: t('web:aiPlan.unresolved.doneSub.skipped') };
    case 'added':
      return {
        title: t('web:aiPlan.unresolved.done.added', { name }),
        sub: t(`web:aiPlan.unresolved.doneSub.${outcome.how ?? 'added'}`),
      };
    case 'picked':
      return {
        title: t('web:aiPlan.unresolved.done.picked', { name, target: outcome.target }),
        sub: t('web:aiPlan.unresolved.doneSub.picked'),
      };
  }
}

/**
 * Board D's sentence after a quick add that failed, by why it failed. The owner's own copy is the
 * network's; a refusal points at the form, where the values can be corrected, and a server failure
 * asks for a moment.
 */
export function addFailureWords(t: TFunction, failure: AddFailure): { readonly lead: string; readonly rest: string } {
  const rest = failure === 'network' ? t('web:aiPlan.unresolved.failedRest') : failure === 'rejected' ? t('web:aiPlan.unresolved.failedRejected') : t('web:aiPlan.unresolved.failedServer');
  return { lead: t('web:aiPlan.unresolved.failedLead'), rest };
}
